/**
 * Enh 5 - Security Settings (/settings/security).
 * Change password: current + new + confirm. Backend enforces rate-limit + audit-log.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Lock, ShieldCheck } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const SecuritySettingsPage = () => {
  const { user } = useAuth();
  const [cur, setCur] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setMsg('');
    if (pw1.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (pw1 !== pw2)     { setErr('Passwords do not match.'); return; }
    if (!cur)            { setErr('Current password is required.'); return; }
    setBusy(true);
    try {
      await api.post('/auth/set-password', { current_password: cur, new_password: pw1 });
      setMsg('Password updated.');
      setCur(''); setPw1(''); setPw2('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not update password.');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto p-8" data-testid="security-settings-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest">
          <ShieldCheck className="h-4 w-4" /> Security
        </div>
        <h1 className="font-display text-3xl font-bold text-primary-900 mt-1">Change password</h1>
        <p className="text-neutral-600 text-sm mt-1">
          Signed in as <span className="font-semibold">{user && user.email}</span>. Password changes are rate-limited to 5 per hour and audit-logged.
        </p>
      </div>
      <Card className="border shadow-soft">
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-3 max-w-md">
            <div>
              <Label className="text-xs uppercase tracking-widest text-secondary-700 flex items-center gap-1.5">
                <Lock className="h-3 w-3" /> Current password
              </Label>
              <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                     required data-testid="change-pw-current" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-secondary-700">New password</Label>
              <Input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
                     placeholder="At least 8 characters" required data-testid="change-pw-new" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-secondary-700">Confirm new password</Label>
              <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                     required data-testid="change-pw-confirm" />
            </div>
            {err && <div className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded px-3 py-2" data-testid="change-pw-error">{err}</div>}
            {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2" data-testid="change-pw-msg">{msg}</div>}
            <Button type="submit" disabled={busy}
                    className="bg-primary-800 hover:bg-primary-900 text-white"
                    data-testid="change-pw-submit">
              {busy ? 'Updating...' : 'Update password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettingsPage;
